import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Plus, ChevronDown, ChevronUp, Square, AlertTriangle } from 'lucide-react';
import { getOrders, createOrder, stopOrder, type IpdMedicationOrder, type CreateOrderPayload } from '@/lib/ipdApi';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import DrugSearchInput from '@/components/shared/DrugSearchInput';
import type { DrugSearchResult } from '@/lib/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const ROUTES   = ['oral', 'iv', 'im', 'sc', 'topical', 'inhalation', 'other'] as const;
const FREQS    = ['OD', 'BD', 'TDS', 'QID', 'Q6H', 'SOS'] as const;

// ─── Order Form ───────────────────────────────────────────────────────────────

interface OrderFormState {
  drug_id:               number | null;
  drug_name:             string;
  generic_name:          string;
  dose:                  string;
  route:                 string;
  frequency:             string;
  start_date:            string;
  end_date:              string;
  is_prn:                boolean;
  prn_reason:            string;
  special_instructions:  string;
}

const today = () => format(new Date(), 'yyyy-MM-dd');

const emptyForm = (): OrderFormState => ({
  drug_id: null, drug_name: '', generic_name: '',
  dose: '', route: 'oral', frequency: 'BD',
  start_date: today(), end_date: '',
  is_prn: false, prn_reason: '', special_instructions: '',
});

// ─── Small shared UI ──────────────────────────────────────────────────────────

function Select({
  value, onChange, options, className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={cn(
        'h-8 rounded-md border border-input bg-background px-2 text-sm',
        'focus:outline-none focus:ring-1 focus:ring-ring',
        className,
      )}
    >
      {options.map(o => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

// ─── Stop inline flow ─────────────────────────────────────────────────────────

function StopRow({
  order, onStop,
}: {
  order: IpdMedicationOrder;
  onStop: (orderId: string, reason: string) => Promise<void>;
}) {
  const [open,   setOpen]   = useState(false);
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState('');

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onStop(order.id, reason.trim());
      setOpen(false);
      setReason('');
    } catch (e: any) {
      setError(e?.message || 'Failed to stop order');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={() => setOpen(true)}
      >
        <Square className="w-3 h-3 mr-1" /> Stop
      </Button>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          placeholder="Reason…"
          value={reason}
          onChange={e => setReason(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setOpen(false); }}
          className="h-7 text-xs w-36"
        />
        <Button size="sm" className="h-7 px-2 text-xs" onClick={submit} disabled={busy || !reason.trim()}>
          {busy ? '…' : 'Confirm'}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setOpen(false); setError(''); }}>
          ✕
        </Button>
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  admissionId: string;
  isActive?: boolean;
}

export default function IPDMedicationOrdersTab({ admissionId, isActive = true }: Props) {
  const { currentUser } = useAuth();
  // Can create medication orders if has clinical profile (doctor_id) — both doctors and doctor-admins
  const isDoctor = !!currentUser?.doctor_id;

  const [orders,      setOrders]      = useState<IpdMedicationOrder[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [showStopped, setShowStopped] = useState(false);
  const [open,        setOpen]        = useState(false);
  const [form,        setForm]        = useState<OrderFormState>(emptyForm());
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOrders(admissionId, 'all');
      setOrders(data ?? []);
    } catch (e) {
      console.error('Failed to load orders:', e);
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const active  = orders.filter(o => o.status === 'active');
  const stopped = orders.filter(o => o.status !== 'active');

  const set = <K extends keyof OrderFormState>(k: K) =>
    (v: OrderFormState[K]) => setForm(f => ({ ...f, [k]: v }));

  const handleDrugSelect = (drug: DrugSearchResult) => {
    setForm(f => ({
      ...f,
      drug_id:      drug.id ?? null,
      drug_name:    drug.brand_name,
      generic_name: drug.composition_raw ?? '',
    }));
  };

  const handleDrugCustom = (val: string) => {
    setForm(f => ({ ...f, drug_id: null, drug_name: val, generic_name: '' }));
  };

  const handleSave = async () => {
    setError('');
    if (!form.drug_name.trim())  { setError('Drug name is required.');    return; }
    if (!form.dose.trim())       { setError('Dose is required.');          return; }
    if (!form.frequency.trim())  { setError('Frequency is required.');     return; }
    if (!form.start_date)        { setError('Start date is required.');    return; }
    if (form.is_prn && !form.prn_reason.trim()) {
      setError('PRN reason is required when PRN is on.');
      return;
    }

    const payload: CreateOrderPayload = {
      drug_id:              form.drug_id,
      drug_name:            form.drug_name.trim(),
      generic_name:         form.generic_name.trim() || undefined,
      dose:                 form.dose.trim(),
      route:                form.route,
      frequency:            form.frequency.trim(),
      start_date:           form.start_date,
      end_date:             form.end_date || null,
      is_prn:               form.is_prn,
      prn_reason:           form.is_prn ? form.prn_reason.trim() : undefined,
      special_instructions: form.special_instructions.trim() || undefined,
    };

    try {
      setSaving(true);
      await createOrder(admissionId, payload);
      setOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save order.');
    } finally {
      setSaving(false);
    }
  };

  const handleStop = async (orderId: string, reason: string) => {
    await stopOrder(admissionId, orderId, reason);
    await load();
  };

  // ── Shared table render ─────────────────────────────────────────────────────

  const OrderTable = ({ rows, showStopBtn }: { rows: IpdMedicationOrder[]; showStopBtn: boolean }) => (
    <div className="rounded-md border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40">
            {['Drug', 'Dose', 'Route', 'Frequency', 'Start', 'End', 'By', showStopBtn ? 'Action' : 'Status'].map((h, i) => (
              <th key={i} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((o, i) => (
            <tr key={o.id} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
              <td className="px-3 py-2">
                <div className="font-medium text-sm">{o.drug_name}</div>
                {o.generic_name && (
                  <div className="text-xs text-muted-foreground">{o.generic_name}</div>
                )}
                {o.is_prn ? (
                  <span className="text-xs bg-amber-100 text-amber-700 rounded px-1">PRN</span>
                ) : null}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">{o.dose}</td>
              <td className="px-3 py-2 whitespace-nowrap capitalize">{o.route}</td>
              <td className="px-3 py-2 whitespace-nowrap">{o.frequency}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs">{o.start_date ? format(new Date(o.start_date), 'dd MMM') : '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs">{o.end_date ? format(new Date(o.end_date), 'dd MMM') : '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{o.ordered_by_name}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {showStopBtn && isDoctor && isActive ? (
                  <StopRow order={o} onStop={handleStop} />
                ) : (
                  <span className={cn(
                    'text-xs px-2 py-0.5 rounded-full',
                    o.status === 'stopped'   ? 'bg-red-100 text-red-700'   :
                    o.status === 'completed' ? 'bg-green-100 text-green-700' :
                    'bg-blue-100 text-blue-700'
                  )}>
                    {o.status}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {active.length} active order{active.length !== 1 ? 's' : ''}
        </div>
        {isDoctor && isActive && (
          <Button size="sm" onClick={() => { setForm(emptyForm()); setError(''); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New Order
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : (
        <>
          {/* Active orders */}
          {active.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No active orders.</div>
          ) : (
            <OrderTable rows={active} showStopBtn={true} />
          )}

          {/* Stopped / completed — collapsible */}
          {stopped.length > 0 && (
            <div>
              <button
                onClick={() => setShowStopped(s => !s)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                {showStopped ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {stopped.length} stopped / completed order{stopped.length !== 1 ? 's' : ''}
              </button>
              {showStopped && <OrderTable rows={stopped} showStopBtn={false} />}
            </div>
          )}
        </>
      )}

      {/* New Order Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Medication Order</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">

            {/* Drug search */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Drug</label>
              <div className="border rounded-md px-2 py-1.5 bg-background focus-within:ring-1 focus-within:ring-ring">
                <DrugSearchInput
                  value={form.drug_name}
                  onChange={handleDrugSelect}
                  onCustomInput={handleDrugCustom}
                  placeholder="Search or type drug name…"
                />
              </div>
            </div>

            {/* Dose */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Dose</label>
              <Input
                value={form.dose}
                onChange={e => set('dose')(e.target.value)}
                placeholder="e.g. 500mg, 1 unit"
                className="h-8 text-sm"
              />
            </div>

            {/* Route + Frequency */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Route</label>
                <Select value={form.route} onChange={set('route')} options={ROUTES} className="w-full" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Frequency</label>
                <Select value={form.frequency} onChange={set('frequency')} options={FREQS} className="w-full" />
              </div>
            </div>

            {/* Start + End date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Start Date</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={e => set('start_date')(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">End Date <span className="text-muted-foreground/60">(optional)</span></label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={e => set('end_date')(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* PRN toggle */}
            <div className="flex items-center gap-2">
              <input
                id="prn-toggle"
                type="checkbox"
                checked={form.is_prn}
                onChange={e => set('is_prn')(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <label htmlFor="prn-toggle" className="text-sm select-none cursor-pointer">PRN (as needed)</label>
            </div>

            {form.is_prn && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">PRN Reason</label>
                <Input
                  value={form.prn_reason}
                  onChange={e => set('prn_reason')(e.target.value)}
                  placeholder="e.g. pain, fever"
                  className="h-8 text-sm"
                />
              </div>
            )}

            {/* Special instructions */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Special Instructions <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Input
                value={form.special_instructions}
                onChange={e => set('special_instructions')(e.target.value)}
                placeholder="e.g. give with food, dilute in 100ml NS"
                className="h-8 text-sm"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
